import {
	apiPlayerDetails,
	apiPlayerList,
	apiPlayerGameRequest,
	apiPlayerConsoleGamesRequest,
	consoleList,
	apiPlayerGameFetchRequest
} from '../../Redux/Types';
import { PeerGamingApiSlice } from '../PeerGamingApiSlice';
import { PlayerGame, PlayerSocial, ProfileIcon } from './types';

export const playersSlice = PeerGamingApiSlice.injectEndpoints({
	endpoints(builder) {
		return {
			fetchPlayers: builder.query<apiPlayerList[], undefined>({
				query() {
					return '/Players';
				},
				providesTags: (response) => {
					const players = response?.map(({ username }) => ({
						type: 'Player' as const,
						id: username
					}));

					return players ? ['Player', ...players] : ['Player'];
				},
				transformResponse: (result: apiPlayerList[]) =>
					result.sort((left, right) =>
						left.username.localeCompare(right.username)
					)
			}),

			fetchPlayerDetails: builder.query<apiPlayerDetails, string>({
				query: (username) => ({
					url: `/Players/${username}`
				}),
				providesTags: (_result, _error, username) => {
					return [{ type: 'Player', username }]; // Each combination of username caches to a specific id
				}
			}),

			fetchPlayerGames: builder.query<
				PlayerGame[],
				apiPlayerGameFetchRequest
			>({
				query: (payload) => ({
					url: `/Players/${payload.username}/Games`,
					params: {
						platform: payload.name,
						gameTitleContains: payload.gameTitleContains
					}
				}),
				providesTags: (_result, _error, request) => {
					const tagId = `${request.username}-${request.name}`;
					return [{ type: 'PlayerGames', id: tagId }];
				}
			}),

			addPlayerGame: builder.mutation<
				apiPlayerGameRequest,
				{ username: string; body: apiPlayerGameRequest }
			>({
				query: (payload) => ({
					url: `/Players/${payload.username}/Games`,
					method: 'POST',
					body: payload.body
				}),
				invalidatesTags: (_result, _error, request) => {
					//Tag represents the users console
					const tagId = `${request.username}-${request.body.name}`;
					return [{ type: 'PlayerGames', id: tagId }];
				}
			}),

			deletePlayerGame: builder.mutation<
				// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
				void,
				{ username: string; body: apiPlayerGameRequest }
			>({
				query: (payload) => ({
					url: `/Players/${payload.username}/Games/${payload.body.title}`,
					params: {
						platform: payload.body.name
					},
					method: 'DELETE'
				}),
				invalidatesTags: (_result, _error, request) => {
					const tagId = `${request.username}-${request.body.name}`;
					return [{ type: 'PlayerGames', id: tagId }];
				}
			}),

			/** Player Platform endpoints */

			fetchPlayerPlatforms: builder.query<consoleList[], string>({
				query: (username) => ({
					url: `/Players/${username}/Platforms`
				}),
				providesTags: (_result, _error, username) => {
					return [{ type: 'PlayerPlatforms', id: username }];
				},
				transformResponse: (result: consoleList[]) =>
					result.sort((left, right) =>
						left.name.localeCompare(right.name)
					)
			}),

			addPlayerPlatform: builder.mutation<
				boolean,
				{ username: string; body: apiPlayerConsoleGamesRequest }
			>({
				query: (payload) => ({
					url: `/Players/${payload.username}/Platforms`,
					method: 'POST',
					body: payload.body
				}),
				invalidatesTags: (_result, _error, playerPlatform) => {
					return [
						{ type: 'PlayerPlatforms', id: playerPlatform.username }
					];
				}
			}),

			deletePlayerPlatform: builder.mutation<
				boolean,
				{ username: string; body: apiPlayerConsoleGamesRequest }
			>({
				query: (payload) => ({
					url: `/Players/${payload.username}/Platforms/${payload.body.name}`,
					method: 'DELETE'
				}),
				invalidatesTags: (_result, _error, playerPlatform) => {
					return [
						{ type: 'PlayerPlatforms', id: playerPlatform.username }
					];
				}
			}),

			fetchPlayerSocials: builder.query<PlayerSocial[], string>({
				query: (username) => ({
					url: `/Players/${username}/socials`
				}),
				providesTags: (_result, _error, username) => [
					{ type: 'PlayerSocials', username }
				]
			}),

			addPlayerSocial: builder.mutation<
				PlayerSocial,
				{ id: number; socialUsername: string; username: string }
			>({
				query: (request) => ({
					url: `/Players/${request.username}/socials`,
					method: 'Post',
					body: {
						id: request.id,
						socialUsername: request.socialUsername
					}
				}),
				invalidatesTags: (_result, _error, username) => [
					{ type: 'PlayerSocials', username }
				]
			}),

			deletePlayerSocial: builder.mutation<
				// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
				void,
				{ id: number; username: string }
			>({
				query: (request) => ({
					url: `/Players/${request.username}/socials/${String(
						request.id
					)}`,
					method: 'DELETE'
				}),
				invalidatesTags: (_result, _error, username) => [
					{ type: 'PlayerSocials', username }
				]
			}),

			fetchProfileIcons: builder.query<ProfileIcon[], undefined>({
				query: () => '/Players/icons',
				providesTags: ['ProfileIcons']
			}),

			setProfileIcon: builder.mutation<
				// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
				void,
				{ username: string; iconFileName: string }
			>({
				query: (request) => ({
					url: `/Players/${request.username}/icon`,
					method: 'POST',
					body: {
						iconFileName: request.iconFileName
					}
				}),
				invalidatesTags: (_response, _meta, { username }) => [
					{ type: 'Player', username }
				]
			})
		};
	}
});

export const {
	useFetchPlayerDetailsQuery,
	useFetchPlayersQuery,
	useAddPlayerGameMutation,
	useFetchPlayerGamesQuery,
	useDeletePlayerGameMutation,
	useFetchPlayerPlatformsQuery,
	useAddPlayerPlatformMutation,
	useDeletePlayerPlatformMutation,
	useFetchPlayerSocialsQuery,
	useAddPlayerSocialMutation,
	useDeletePlayerSocialMutation,
	useFetchProfileIconsQuery,
	useSetProfileIconMutation
} = playersSlice;
